import type { Infer } from 'spacetimedb';
import { reducers, DbConnection, MyInventoryRow, MyItemBarRow, MyEquipmentRow, MyStorageRow, CurrentMapRuntimeNpcsRow, AccessibleChestInventoryRow, type ErrorContext, type SubscriptionEventContext } from '../generated';
import type { ConnectionManager } from '../core/ConnectionManager';
import type { ReducerClient } from '../core/ReducerClient';
import type {
  InventorySlotRecord,
  ItemBarSlotRecord,
  EquipmentSlotRecord,
  StorageSlotRecord,
  StorageSnapshot,
  NpcRuntimeRecord,
  ChestInventoryRecord,
  ActorInventoryRecord,
  Logger
} from '../types';

type MyInventoryTableRow = Infer<typeof MyInventoryRow>;
type MyItemBarTableRow = Infer<typeof MyItemBarRow>;
type MyEquipmentTableRow = Infer<typeof MyEquipmentRow>;
type MyStorageTableRow = Infer<typeof MyStorageRow>;
type CurrentMapRuntimeNpcsTableRow = Infer<typeof CurrentMapRuntimeNpcsRow>;
type AccessibleChestInventoryTableRow = Infer<typeof AccessibleChestInventoryRow>;

export class InventoryModule {
  constructor(
    private readonly connectionManager: ConnectionManager,
    private readonly reducerClient: ReducerClient,
    private readonly logger: Logger = () => undefined
  ) { }

  public async fetchPlayerInventory(): Promise<InventorySlotRecord[]> {
    const connection = await this.connectionManager.ensureConnection();
    const rows = await this.runInventoryViewQuery(connection);

    return rows.map((slot) => ({
      inventoryId: slot.inventoryId,
      slotIndex: Number(slot.slotIndex ?? 0),
      itemId: slot.itemId,
      quantity: Number(slot.quantity ?? 0),
      tradeable: Boolean((slot as MyInventoryTableRow & { tradeable?: boolean | null }).tradeable ?? true),
      metadata: this.parseJson<Record<string, unknown>>(slot.metadataJson, {})
    }));
  }

  public async subscribeToInventory(
    onUpdate: (items: InventorySlotRecord[]) => void
  ): Promise<{ unsubscribe: () => void }> {
    const connection = await this.connectionManager.ensureConnection();
    const inventoryView = connection.db.myInventory;

    const emitRows = (): void => {
      const rows = Array.from(inventoryView.iter()).map((slot) => ({
        inventoryId: slot.inventoryId,
        slotIndex: Number(slot.slotIndex ?? 0),
        itemId: slot.itemId,
        quantity: Number(slot.quantity ?? 0),
        tradeable: Boolean((slot as MyInventoryTableRow & { tradeable?: boolean | null }).tradeable ?? true),
        metadata: this.parseJson<Record<string, unknown>>(slot.metadataJson, {})
      }));
      // Sort by slot index for stable ordering
      rows.sort((a, b) => a.slotIndex - b.slotIndex);

      onUpdate(rows);
    };

    const handleChange = (): void => {
      emitRows();
    };

    inventoryView.onInsert(handleChange);
    inventoryView.onDelete(handleChange);
    inventoryView.onUpdate(handleChange);

    // Initial emit
    emitRows();

    const builder = connection.subscriptionBuilder();
    const sql = 'SELECT * FROM my_inventory';

    return new Promise((resolve, reject) => {
      let handle: ReturnType<typeof builder.subscribe> | null = null;
      let listenersRemoved = false;

      const cleanup = (): void => {
        if (listenersRemoved) return;
        listenersRemoved = true;
        inventoryView.removeOnInsert(handleChange);
        inventoryView.removeOnDelete(handleChange);
        inventoryView.removeOnUpdate(handleChange);
      };

      builder
        .onApplied(() => {
          try {
            emitRows();
            resolve({
              unsubscribe: () => {
                cleanup();
                handle?.unsubscribe();
              }
            });
          } catch (error) {
            cleanup();
            handle?.unsubscribe();
            reject(error as Error);
          }
        })
        .onError((ctx: ErrorContext) => {
          const error = ctx.event ?? new Error('Inventory subscription failed');
          cleanup();
          handle?.unsubscribe();
          reject(error);
        });

      handle = builder.subscribe(sql);
    });
  }

  public async fetchItemBar(): Promise<ItemBarSlotRecord[]> {
    const connection = await this.connectionManager.ensureConnection();
    const rows = await this.runItemBarViewQuery(connection);

    return rows.map((slot) => ({
      barIndex: Number(slot.barIndex ?? 0),
      inventoryId: slot.inventoryId,
      itemId: slot.itemId,
      quantity: Number(slot.quantity ?? 0),
      tradeable: Boolean((slot as MyItemBarTableRow & { tradeable?: boolean | null }).tradeable ?? true),
      metadata: this.parseJson<Record<string, unknown>>(slot.metadataJson, {})
    }));
  }

  public async subscribeToItemBar(
    onUpdate: (items: ItemBarSlotRecord[]) => void
  ): Promise<{ unsubscribe: () => void }> {
    const connection = await this.connectionManager.ensureConnection();
    const itemBarView = connection.db.myItemBar;

    const emitRows = (): void => {
      const rows = Array.from(itemBarView.iter()).map((slot) => ({
        barIndex: Number(slot.barIndex ?? 0),
        inventoryId: slot.inventoryId,
        itemId: slot.itemId,
        quantity: Number(slot.quantity ?? 0),
        tradeable: Boolean((slot as MyItemBarTableRow & { tradeable?: boolean | null }).tradeable ?? true),
        metadata: this.parseJson<Record<string, unknown>>(slot.metadataJson, {})
      }));
      rows.sort((a, b) => a.barIndex - b.barIndex);
      onUpdate(rows);
    };

    const handleChange = (): void => {
      emitRows();
    };

    itemBarView.onInsert(handleChange);
    itemBarView.onDelete(handleChange);
    itemBarView.onUpdate(handleChange);

    // Initial emit
    emitRows();

    const builder = connection.subscriptionBuilder();
    const sql = 'SELECT * FROM my_item_bar';

    return new Promise((resolve, reject) => {
      let handle: ReturnType<typeof builder.subscribe> | null = null;
      let listenersRemoved = false;

      const cleanup = (): void => {
        if (listenersRemoved) return;
        listenersRemoved = true;
        itemBarView.removeOnInsert(handleChange);
        itemBarView.removeOnDelete(handleChange);
        itemBarView.removeOnUpdate(handleChange);
      };

      builder
        .onApplied(() => {
          try {
            emitRows();
            resolve({
              unsubscribe: () => {
                cleanup();
                handle?.unsubscribe();
              }
            });
          } catch (error) {
            cleanup();
            handle?.unsubscribe();
            reject(error as Error);
          }
        })
        .onError((ctx: ErrorContext) => {
          const error = ctx.event ?? new Error('Item bar subscription failed');
          cleanup();
          handle?.unsubscribe();
          reject(error);
        });

      handle = builder.subscribe(sql);
    });
  }

  public async addToItemBar(inventoryId: string): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'add_to_item_bar',
      paramsType: reducers.addToItemBar.paramsType,
      args: { inventoryId },
      onEvent: 'onAddToItemBar',
      offEvent: 'removeOnAddToItemBar',
      timeoutMessage: 'Add to item bar timed out',
      failureMessage: 'Failed to add item to bar'
    });
  }

  public async removeFromItemBar(inventoryId: string): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'remove_from_item_bar',
      paramsType: reducers.removeFromItemBar.paramsType,
      args: { inventoryId },
      onEvent: 'onRemoveFromItemBar',
      offEvent: 'removeOnRemoveFromItemBar',
      timeoutMessage: 'Remove from item bar timed out',
      failureMessage: 'Failed to remove item from bar'
    });
  }

  public async moveInventoryItem(inventoryId: string, targetSlotIndex: number): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'move_inventory_item',
      paramsType: reducers.moveInventoryItem.paramsType,
      args: { inventoryId, targetSlotIndex },
      onEvent: 'onMoveInventoryItem',
      offEvent: 'removeOnMoveInventoryItem',
      timeoutMessage: 'Move inventory item timed out',
      failureMessage: 'Failed to move inventory item'
    });
  }

  public async moveChestItem(npcId: string, fromSlotIndex: number, toSlotIndex: number): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'move_chest_item',
      paramsType: reducers.moveChestItem.paramsType,
      args: { npcId, fromSlotIndex, toSlotIndex },
      onEvent: 'onMoveChestItem',
      offEvent: 'removeOnMoveChestItem',
      timeoutMessage: 'Move chest item timed out',
      failureMessage: 'Failed to move chest item'
    });
  }

  public async takeItemFromChestToSlot(
    npcId: string,
    chestSlotIndex: number,
    playerSlotIndex: number
  ): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'take_item_from_chest_to_slot',
      paramsType: reducers.takeItemFromChestToSlot.paramsType,
      args: { npcId, chestSlotIndex, playerSlotIndex },
      onEvent: 'onTakeItemFromChestToSlot',
      offEvent: 'removeOnTakeItemFromChestToSlot',
      timeoutMessage: 'Take chest item to slot timed out',
      failureMessage: 'Failed to take chest item to slot'
    });
  }

  public async splitInventoryItem(inventoryId: string, splitQuantity: number): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'split_inventory_item',
      paramsType: reducers.splitInventoryItem.paramsType,
      args: { inventoryId, splitQuantity },
      onEvent: 'onSplitInventoryItem',
      offEvent: 'removeOnSplitInventoryItem',
      timeoutMessage: 'Split inventory item timed out',
      failureMessage: 'Failed to split inventory item'
    });
  }

  public async splitChestItem(npcId: string, slotIndex: number, splitQuantity: number): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'split_chest_item',
      paramsType: reducers.splitChestItem.paramsType,
      args: { npcId, slotIndex, splitQuantity },
      onEvent: 'onSplitChestItem',
      offEvent: 'removeOnSplitChestItem',
      timeoutMessage: 'Split chest item timed out',
      failureMessage: 'Failed to split chest item'
    });
  }

  public async subscribeToEquipment(
    onUpdate: (slots: EquipmentSlotRecord[]) => void
  ): Promise<{ unsubscribe: () => void }> {
    const connection = await this.connectionManager.ensureConnection();
    const equipmentView = connection.db.myEquipment;

    const emitRows = (): void => {
      const rows = Array.from(equipmentView.iter()).map((slot) => ({
        slotName: slot.slotName,
        inventoryId: slot.inventoryId,
        itemId: slot.itemId,
        quantity: Number(slot.quantity ?? 0),
        tradeable: Boolean((slot as MyEquipmentTableRow & { tradeable?: boolean | null }).tradeable ?? true),
        metadata: this.parseJson<Record<string, unknown>>(slot.metadataJson, {})
      }));
      rows.sort((a, b) => a.slotName.localeCompare(b.slotName));
      onUpdate(rows);
    };

    const handleChange = (): void => { emitRows(); };
    equipmentView.onInsert(handleChange);
    equipmentView.onDelete(handleChange);
    equipmentView.onUpdate(handleChange);
    try {
      emitRows();
    } catch (err) {
      equipmentView.removeOnInsert(handleChange);
      equipmentView.removeOnDelete(handleChange);
      equipmentView.removeOnUpdate(handleChange);
      throw err;
    }

    const builder = connection.subscriptionBuilder();
    const sql = 'SELECT * FROM my_equipment';

    return new Promise((resolve, reject) => {
      let handle: ReturnType<typeof builder.subscribe> | null = null;
      let listenersRemoved = false;

      const cleanup = (): void => {
        if (listenersRemoved) return;
        listenersRemoved = true;
        equipmentView.removeOnInsert(handleChange);
        equipmentView.removeOnDelete(handleChange);
        equipmentView.removeOnUpdate(handleChange);
      };

      builder
        .onApplied(() => {
          try {
            emitRows();
            resolve({
              unsubscribe: () => {
                cleanup();
                handle?.unsubscribe();
              }
            });
          } catch (error) {
            cleanup();
            handle?.unsubscribe();
            reject(error as Error);
          }
        })
        .onError((ctx: ErrorContext) => {
          const error = ctx.event ?? new Error('Equipment subscription failed');
          cleanup();
          handle?.unsubscribe();
          reject(error);
        });

      handle = builder.subscribe(sql);
    });
  }

  public async subscribeToStorage(
    onUpdate: (snapshot: StorageSnapshot) => void
  ): Promise<{ unsubscribe: () => void }> {
    const connection = await this.connectionManager.ensureConnection();
    const storageView = connection.db.myStorage;

    const emitRows = (): void => {
      const viewRows = Array.from(storageView.iter());
      const rows = viewRows.map((slot) => ({
        storageId: slot.storageId,
        slotIndex: Number(slot.slotIndex ?? 0),
        itemId: slot.itemId,
        quantity: Number(slot.quantity ?? 0),
        tradeable: Boolean((slot as MyStorageTableRow & { tradeable?: boolean | null }).tradeable ?? true),
        metadata: this.parseJson<Record<string, unknown>>(slot.metadataJson, {}),
      }));
      rows.sort((a, b) => a.slotIndex - b.slotIndex);
      onUpdate({ slots: rows });
    };

    const handleChange = (): void => { emitRows(); };
    storageView.onInsert(handleChange);
    storageView.onDelete(handleChange);
    storageView.onUpdate(handleChange);

    const builder = connection.subscriptionBuilder();
    const sql = 'SELECT * FROM my_storage';

    return new Promise((resolve, reject) => {
      let handle: ReturnType<typeof builder.subscribe> | null = null;
      let listenersRemoved = false;

      const cleanup = (): void => {
        if (listenersRemoved) return;
        listenersRemoved = true;
        storageView.removeOnInsert(handleChange);
        storageView.removeOnDelete(handleChange);
        storageView.removeOnUpdate(handleChange);
      };

      builder
        .onApplied(() => {
          try {
            emitRows();
            resolve({
              unsubscribe: () => {
                cleanup();
                handle?.unsubscribe();
              }
            });
          } catch (error) {
            cleanup();
            handle?.unsubscribe();
            reject(error as Error);
          }
        })
        .onError((ctx: ErrorContext) => {
          const error = ctx.event ?? new Error('Storage subscription failed');
          cleanup();
          handle?.unsubscribe();
          reject(error);
        });

      handle = builder.subscribe(sql);
    });
  }

  public async equipItem(slotName: string, inventoryId: string): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'equip_item',
      paramsType: reducers.equipItem.paramsType,
      args: { slotName, inventoryId },
      onEvent: 'onEquipItem',
      offEvent: 'removeOnEquipItem',
      timeoutMessage: 'Equip item timed out',
      failureMessage: 'Failed to equip item'
    });
  }

  public async unequipItem(slotName: string): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'unequip_item',
      paramsType: reducers.unequipItem.paramsType,
      args: { slotName },
      onEvent: 'onUnequipItem',
      offEvent: 'removeOnUnequipItem',
      timeoutMessage: 'Unequip item timed out',
      failureMessage: 'Failed to unequip item'
    });
  }

  public async moveInventoryToStorage(inventoryId: string, targetSlotIndex: number): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'move_inventory_to_storage',
      paramsType: reducers.moveInventoryToStorage.paramsType,
      args: { inventoryId, targetSlotIndex },
      onEvent: 'onMoveInventoryToStorage',
      offEvent: 'removeOnMoveInventoryToStorage',
      timeoutMessage: 'Move inventory item to storage timed out',
      failureMessage: 'Failed to move inventory item to storage'
    });
  }

  public async moveStorageToInventory(storageId: string, targetSlotIndex: number): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'move_storage_to_inventory',
      paramsType: reducers.moveStorageToInventory.paramsType,
      args: { storageId, targetSlotIndex },
      onEvent: 'onMoveStorageToInventory',
      offEvent: 'removeOnMoveStorageToInventory',
      timeoutMessage: 'Move storage item to inventory timed out',
      failureMessage: 'Failed to move storage item to inventory'
    });
  }

  public async moveStorageItem(storageId: string, targetSlotIndex: number): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'move_storage_item',
      paramsType: reducers.moveStorageItem.paramsType,
      args: { storageId, targetSlotIndex },
      onEvent: 'onMoveStorageItem',
      offEvent: 'removeOnMoveStorageItem',
      timeoutMessage: 'Move storage item timed out',
      failureMessage: 'Failed to move storage item'
    });
  }

  public async subscribeToNpcRuntime(
    onUpdate: (npcs: NpcRuntimeRecord[]) => void
  ): Promise<{ unsubscribe: () => void }> {
    const connection = await this.connectionManager.ensureConnection();
    const table = connection.db.currentMapRuntimeNpcs;

    const emitRows = (): void => {
      const rows = Array.from(table.iter()).map((row: CurrentMapRuntimeNpcsTableRow) => ({
        npcId: row.npcId,
        instanceId: row.instanceId,
        mapId: row.mapId,
        x: row.x,
        y: row.y,
        npcType: row.npcType,
        metadataJson: row.metadataJson,
        despawnTick: Number(row.despawnTick),
      }));
      onUpdate(rows);
    };

    const handleChange = (): void => { emitRows(); };

    table.onInsert(handleChange);
    table.onDelete(handleChange);
    table.onUpdate(handleChange);

    emitRows();

    const builder = connection.subscriptionBuilder();
    const sql = 'SELECT * FROM current_map_runtime_npcs';

    return new Promise((resolve, reject) => {
      let handle: ReturnType<typeof builder.subscribe> | null = null;
      let listenersRemoved = false;

      const cleanup = (): void => {
        if (listenersRemoved) return;
        listenersRemoved = true;
        table.removeOnInsert(handleChange);
        table.removeOnDelete(handleChange);
        table.removeOnUpdate(handleChange);
      };

      builder
        .onApplied(() => {
          try {
            emitRows();
            resolve({
              unsubscribe: () => {
                cleanup();
                handle?.unsubscribe();
              }
            });
          } catch (error) {
            cleanup();
            handle?.unsubscribe();
            reject(error as Error);
          }
        })
        .onError((ctx: ErrorContext) => {
          const error = ctx.event ?? new Error('NPC runtime subscription failed');
          cleanup();
          handle?.unsubscribe();
          reject(error);
        });

      handle = builder.subscribe(sql);
    });
  }

  public async subscribeToChestInventory(
    onUpdate: (rows: ChestInventoryRecord[]) => void
  ): Promise<{ unsubscribe: () => void }> {
    const connection = await this.connectionManager.ensureConnection();
    const table = connection.db.accessibleChestInventory;

    const emitRows = (): void => {
      const rows = Array.from(table.iter()).map((row: AccessibleChestInventoryTableRow) => ({
        entryId: row.entryId,
        npcId: row.npcId,
        slotIndex: row.slotIndex,
        itemId: row.itemId,
        quantity: row.quantity,
        metadataJson: row.metadataJson,
      }));
      onUpdate(rows);
    };

    const handleChange = (): void => { emitRows(); };

    table.onInsert(handleChange);
    table.onDelete(handleChange);
    table.onUpdate(handleChange);

    emitRows();

    const builder = connection.subscriptionBuilder();
    const sql = 'SELECT * FROM accessible_chest_inventory';

    return new Promise((resolve, reject) => {
      let handle: ReturnType<typeof builder.subscribe> | null = null;
      let listenersRemoved = false;

      const cleanup = (): void => {
        if (listenersRemoved) return;
        listenersRemoved = true;
        table.removeOnInsert(handleChange);
        table.removeOnDelete(handleChange);
        table.removeOnUpdate(handleChange);
      };

      builder
        .onApplied(() => {
          try {
            emitRows();
            resolve({
              unsubscribe: () => {
                cleanup();
                handle?.unsubscribe();
              }
            });
          } catch (error) {
            cleanup();
            handle?.unsubscribe();
            reject(error as Error);
          }
        })
        .onError((ctx: ErrorContext) => {
          const error = ctx.event ?? new Error('Chest inventory subscription failed');
          cleanup();
          handle?.unsubscribe();
          reject(error);
        });

      handle = builder.subscribe(sql);
    });
  }

  public async fetchVisibleActorInventory(): Promise<ActorInventoryRecord[]> {
    const connection = await this.connectionManager.ensureConnection();
    const sql = 'SELECT * FROM visible_actor_inventory';
    const builder = connection.subscriptionBuilder();

    return new Promise((resolve, reject) => {
      let handle: ReturnType<typeof builder.subscribe> | null = null;
      let settled = false;

      const mapRows = (): ActorInventoryRecord[] => {
        const table = (connection.db as unknown as Record<string, { iter(): Iterable<Record<string, unknown>> }>)
          .visibleActorInventory
          ?? (connection.db as unknown as Record<string, { iter(): Iterable<Record<string, unknown>> }>)
            .visible_actor_inventory;
        if (!table) return [];
        return Array.from(table.iter()).map((row) => {
          const rowAny = row as Record<string, unknown>;
          return {
            actorId: String(rowAny.actorId ?? rowAny.actor_id ?? ''),
            ownerKind: String(rowAny.ownerKind ?? rowAny.owner_kind ?? ''),
            slotIndex: Number(rowAny.slotIndex ?? rowAny.slot_index ?? 0),
            itemId: String(rowAny.itemId ?? rowAny.item_id ?? ''),
            quantity: Number(rowAny.quantity ?? 0),
            metadata: this.parseJson<Record<string, unknown>>(
              typeof rowAny.metadataJson === 'string'
                ? rowAny.metadataJson
                : (typeof rowAny.metadata_json === 'string' ? rowAny.metadata_json : '{}'),
              {}
            ),
          };
        });
      };

      builder
        .onApplied(() => {
          if (settled) return;
          settled = true;
          const rows = mapRows();
          if (handle?.unsubscribeThen) {
            handle.unsubscribeThen(() => resolve(rows));
          } else {
            handle?.unsubscribe();
            resolve(rows);
          }
        })
        .onError((ctx: ErrorContext) => {
          if (settled) return;
          settled = true;
          handle?.unsubscribe();
          reject(ctx.event ?? new Error('visible_actor_inventory query failed'));
        });
      handle = builder.subscribe(sql);
    });
  }

  private async runInventoryViewQuery(
    connection: DbConnection
  ): Promise<Array<{ inventoryId: string; slotIndex: number; itemId: string; quantity: number; metadataJson: string }>> {
    const sql = 'SELECT * FROM my_inventory';

    return new Promise((resolve, reject) => {
      const builder = connection.subscriptionBuilder();
      type Handle = ReturnType<typeof builder.subscribe>;
      let handle: Handle | null = null;
      let settled = false;

      builder
        .onApplied((ctx: SubscriptionEventContext) => {
          try {
            const rows = Array.from(ctx.db.myInventory.iter()).map((row: MyInventoryTableRow) => ({
              inventoryId: row.inventoryId,
              slotIndex: Number(row.slotIndex ?? 0),
              itemId: row.itemId,
              quantity: Number(row.quantity ?? 0),
              metadataJson: row.metadataJson
            }));
            settled = true;
            if (handle?.unsubscribeThen) {
              handle.unsubscribeThen(() => resolve(rows));
            } else {
              handle?.unsubscribe();
              resolve(rows);
            }
          } catch (error) {
            settled = true;
            handle?.unsubscribe();
            reject(error as Error);
          }
        })
        .onError((ctx: ErrorContext) => {
          if (!settled) {
            settled = true;
            handle?.unsubscribe();
            reject(ctx.event ?? new Error('Inventory query failed'));
          }
        });

      handle = builder.subscribe(sql);
    });
  }

  private async runItemBarViewQuery(
    connection: DbConnection
  ): Promise<Array<{ barIndex: number; inventoryId: string; itemId: string; quantity: number; metadataJson: string }>> {
    const sql = 'SELECT * FROM my_item_bar';

    return new Promise((resolve, reject) => {
      const builder = connection.subscriptionBuilder();
      type Handle = ReturnType<typeof builder.subscribe>;
      let handle: Handle | null = null;
      let settled = false;

      builder
        .onApplied((ctx: SubscriptionEventContext) => {
          try {
            const rows = Array.from(ctx.db.myItemBar.iter()).map((row: MyItemBarTableRow) => ({
              barIndex: Number(row.barIndex ?? 0),
              inventoryId: row.inventoryId,
              itemId: row.itemId,
              quantity: Number(row.quantity ?? 0),
              metadataJson: row.metadataJson
            }));
            settled = true;
            if (handle?.unsubscribeThen) {
              handle.unsubscribeThen(() => resolve(rows));
            } else {
              handle?.unsubscribe();
              resolve(rows);
            }
          } catch (error) {
            settled = true;
            handle?.unsubscribe();
            reject(error as Error);
          }
        })
        .onError((ctx: ErrorContext) => {
          if (!settled) {
            settled = true;
            handle?.unsubscribe();
            reject(ctx.event ?? new Error('Item bar query failed'));
          }
        });

      handle = builder.subscribe(sql);
    });
  }

  private parseJson<T>(source: string | null | undefined, fallback: T): T {
    if (!source) {
      return fallback;
    }
    try {
      return JSON.parse(source) as T;
    } catch (error) {
      this.logger('json_parse_failure', {
        error: (error as Error).message,
        sourceSample: source.slice(0, 64)
      });
      return fallback;
    }
  }
}
